import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { PageHeader } from '@platform/ui-kit';
import { api, type SpnDiscoverySession } from '../../../api';
import { HelpPanel, type Help } from './HelpPanel';

/**
 * Strategic Portfolio Navigator → Discoveries.
 *
 * Ported from `spn_template/views/discovery/discovery-landing.ejs`. Title, the New
 * Discovery action, the "Discovery History" heading and the table's eight columns —
 * Session, Company, Round, Status, Opportunities, Saved, Created By, Date — are the
 * source's, in its order.
 *
 * "Opportunities" is every opportunity the session produced; "Saved" is only those
 * already committed to the real opportunities table. Two different numbers that look
 * alike, which is why the source counts them separately and so does the query.
 *
 * NO SESSIONS ARE SEEDED — the owner asked not to bring the source's previous runs
 * across, so this table is empty until a discovery is run here.
 */

const LANDING_HELP: Help = {
  title: 'Strategic Discoveries',
  icon: '🔭',
  purpose:
    'Every discovery run for this company, and where each one got to. A discovery examines the business areas you choose and returns candidate opportunities — findings to triage, not decisions already taken.',
  doHere: [
    'Click <b>+ New Discovery</b> to set up and launch a run.',
    '<b>Resume</b> an open session to keep triaging, or <b>Open</b> a closed one to review what it produced.',
    'Read <b>Opportunities</b> as everything the run returned and <b>Saved</b> as those committed to the real opportunities table — two different numbers that look alike.',
    '<b>Delete</b> a run you no longer want; every opportunity, project and round in it goes too.',
  ],
  fits: 'Strategic Portfolio Navigator &rarr; Discoveries',
  firstTime:
    'Start with one business area and Depth set to scan — five opportunities is enough to see the shape before committing tokens to a wide run.',
};

const money = (v: unknown) => Number(v ?? 0).toLocaleString('en-US');

export function DiscoveriesLanding() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<SpnDiscoverySession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const remove = async (row: SpnDiscoverySession) => {
    if (
      !window.confirm(
        `Delete ${row.session_code}? Every opportunity, project and round in this discovery ` +
          'goes with it. This cannot be undone.',
      )
    )
      return;
    setBusy(true);
    try {
      await api.spn.deleteDiscovery(row.session_code);
      setSessions((prev) => prev.filter((x) => x.id !== row.id));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    let active = true;
    api.spn
      .getDiscoverySessions()
      .then((d) => { if (active) setSessions(d.sessions); })
      .catch((e) => { if (active) setError(e?.message || 'Could not load discovery sessions.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const th: React.CSSProperties = {
    textAlign: 'left',
    fontSize: 12.5,
    fontWeight: 600,
    color: '#1565c0',
    padding: '10px 12px',
    borderBottom: '1px solid #e0e0e0',
    whiteSpace: 'nowrap',
  };
  const td: React.CSSProperties = {
    padding: '12px',
    borderBottom: '1px solid #eef1f6',
    fontSize: 13,
    verticalAlign: 'middle',
  };

  return (
    <>
      {/* "Strategic Discoveries", not the source's "SPR Strategic Discoveries" — the
          owner's wording (2026-08-04). "SPR" was the source app's own initialism and means
          nothing inside this platform, where the product is already named in the nav. */}
      <HelpPanel help={LANDING_HELP} />
      <PageHeader title="Strategic Discoveries" />


      <Box sx={{ mb: 2.5 }}>
        <Button
          variant="contained"
          onClick={() => navigate('/spn/discoveries/new')}
          title="Start a new discovery session"
        >
          + New Discovery
        </Button>
      </Box>

      <Typography sx={{ fontSize: 15, fontWeight: 500, color: '#424242', mb: 1.5 }}>
        Discovery History
      </Typography>

      {error ? (
        <Typography sx={{ fontSize: 13, color: 'error.main', mb: 2 }}>{error}</Typography>
      ) : null}

      {loading ? (
        <Typography sx={{ fontSize: 13, color: '#999' }}>Loading…</Typography>
      ) : sessions.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center', color: '#8a95a8', border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Typography sx={{ fontSize: 14 }}>No discovery sessions yet.</Typography>
          <Typography sx={{ fontSize: 12.5, mt: 0.5 }}>
            Click <strong>New Discovery</strong> to analyze a company and generate candidate
            opportunities.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
            <thead>
              <tr>
                <th style={th}>Session</th>
                <th style={th}>Company</th>
                <th style={th}>Round</th>
                <th style={th}>Status</th>
                <th style={th}>Opportunities</th>
                <th style={th}>Saved</th>
                <th style={th}>Created By</th>
                <th style={th}>Date</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id}>
                  <td style={td}>
                    <strong>{s.session_code}</strong>
                  </td>
                  <td style={td}>
                    <div>{s.resolved_company_name}</div>
                    {s.company_code ? (
                      <div style={{ fontSize: 11.5, color: '#8a95a8' }}>{s.company_code}</div>
                    ) : null}
                  </td>
                  <td style={td}>{s.round_number ?? 1}</td>
                  <td style={td}>
                    <Chip
                      size="small"
                      label={s.status === 'active' ? 'Active' : s.status}
                      color={s.status === 'active' ? 'primary' : 'default'}
                      sx={{ height: 22, fontSize: 11.5, textTransform: 'capitalize' }}
                    />
                  </td>
                  <td style={td}>{money(s.opp_count)}</td>
                  <td style={td}>{money(s.created_count)}</td>
                  <td style={td}>{s.creator_name || '-'}</td>
                  <td style={td}>{new Date(s.created_at).toLocaleDateString()}</td>
                  <td style={td}>
                    {/* The source offers Resume on an open session and Open on a closed
                        one — the same destination, different promise. */}
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => navigate(`/spn/discoveries/${s.session_code}`)}
                      title={
                        s.status === 'active'
                          ? 'Resume this open discovery — continue refining with Claude'
                          : 'Open this discovery to review what it produced'
                      }
                    >
                      {s.status === 'active' ? '▶ Resume' : 'Open'}
                    </Button>{' '}
                    {/* Deleting from the LIST as well as from inside the session: a run you
                        can already tell was wrong should not have to be opened first. */}
                    <Button
                      size="small"
                      color="error"
                      onClick={() => remove(s)}
                      disabled={busy}
                      title="Delete this discovery and everything in it"
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}
    </>
  );
}
